import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, MapPin, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';
import WhatsAppButton from '../components/WhatsAppButton';
import EmailButton from '../components/EmailButton';
import { faqPageSchema, townServiceSchema } from '../lib/schema';
import { customerReviews } from '../data/reviewsData';
import { townBySlug } from '../data/towns';

// Reviews from customers whose postcode district is in this town.
const localReviews = (town) =>
  customerReviews.filter((r) => town.reviewPostcodes.some((pc) => r.location.startsWith(`${pc} `)));

const TownPage = ({ slug }) => {
  const town = townBySlug(slug);
  const reviews = localReviews(town);

  return (
    <div className="min-h-screen bg-white">
      <SEO title={town.title} description={town.description} canonical={town.path} />
      <JsonLd data={townServiceSchema(town)} />
      <JsonLd data={faqPageSchema(town.faqs, town.path)} />

      {/* Hero */}
      <section className="relative pt-36 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-cyan-300 font-medium mb-4">
              <MapPin className="h-5 w-5" /> {town.name}, Kent
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">{town.heading}</h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">{town.intro}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/contact">
                <Button className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-6 h-9">
                  Free Quote
                </Button>
              </Link>
              <WhatsAppButton />
              <EmailButton />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">What we do in {town.name}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {town.services.map((s) => (
              <div key={s.name} className="rounded-2xl border border-slate-200 p-6">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900 mb-2">
                  <CheckCircle className="h-5 w-5 text-cyan-500" /> {s.name}
                </h3>
                <p className="text-slate-600 mb-3">{s.text}</p>
                <Link to={`/services#${s.anchor}`} className="text-cyan-600 hover:text-cyan-700 text-sm font-medium">
                  More about {s.name.toLowerCase()} →
                </Link>
              </div>
            ))}
          </div>
          {town.job && (
            <div className="mt-10 grid md:grid-cols-2 gap-6 items-center rounded-2xl bg-slate-50 p-6">
              <img src={town.job.image} alt={town.job.title} className="w-full h-72 object-cover rounded-xl" loading="lazy" />
              <div>
                <p className="text-sm font-medium text-cyan-600 mb-1">A {town.name} job</p>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{town.job.title}</h3>
                <p className="text-slate-600">{town.job.text}</p>
              </div>
            </div>
          )}
          <p className="mt-8 text-slate-600">
            See finished jobs in our <Link to="/portfolio" className="text-cyan-600 hover:text-cyan-700 font-medium">portfolio</Link>.
          </p>
        </div>
      </section>

      {/* Areas */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Areas we cover in and around {town.name}</h2>
          <ul className="flex flex-wrap gap-2">
            {town.neighbourhoods.map((n) => (
              <li key={n} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm">
                {n}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Local reviews */}
      {reviews.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">What {town.name} customers say</h2>
            <p className="text-slate-600 mb-8">Reviews from customers in {town.name} postcodes, taken from Checkatrade.</p>
            <div className="grid md:grid-cols-2 gap-6">
              {reviews.map((r) => (
                <figure key={r.id} className="rounded-2xl border border-slate-200 p-6">
                  <div className="flex items-center gap-1 text-amber-400 mb-2" aria-label={`${r.score} out of 10`}>
                    {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                    <span className="ml-2 text-sm text-slate-500">{r.score}/10</span>
                  </div>
                  <p className="font-semibold text-slate-900 mb-2">{r.title}</p>
                  <blockquote className="text-slate-600 mb-3">{r.review}</blockquote>
                  <figcaption className="text-sm text-slate-500">
                    {r.customerName}, {r.location}, {r.date}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Questions from {town.name} customers</h2>
          <div className="space-y-6">
            {town.faqs.map((f) => (
              <div key={f.question}>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{f.question}</h3>
                <p className="text-slate-600">{f.answer}</p>
              </div>
            ))}
          </div>
          <Link to="/faq" className="inline-block mt-6 text-cyan-600 hover:text-cyan-700 font-medium">
            More questions answered →
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-cyan-700 text-white text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Get a free quote in {town.name}</h2>
          <p className="text-cyan-100 mb-8">Tell us about the job and we'll arrange a visit and a clear written quote.</p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-cyan-700 hover:bg-slate-100 rounded-full px-8">
              Request your free quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TownPage;
